// Bundles the pan/zoom machinery (Viewport state + pointer/wheel wiring) into
// one small rig, so GameScene and LessonScene each need only construct/
// dispose it and read `.viewport` for `renderScene` — neither scene
// re-implements the gesture plumbing. The reset-view button itself is drawn
// ON CANVAS by `renderScene` (see `render/ViewportControls.ts`, ported from
// domino) — this rig only supplies the hit-test the pointer machine needs to
// treat it as a tap target instead of a pan start. Mouse hover also routes
// through here, driving the tile-name tooltip + pointer cursor (see
// `findHoveredTile` / `ui/TileHoverCursor`). The pause gear lives here whole —
// drawn, hit-tested and labelled — since it is the same in both scenes.

import type { MahjongState } from '../game/GameState.js';
import type { Seat } from '../game/types.js';
import { seatPosition } from '../layout/types.js';
import { InputHandler } from '../input/InputHandler.js';
import { ViewportController, type CanvasButton, type Point } from '../input/ViewportController.js';
import { Viewport } from '../render/Viewport.js';
import { CanvasTooltip } from '@bn-games/ui-shell';
import { drawPauseGear, gearHitTest, pauseGearBounds } from '@bn-games/render-helpers';
import { t } from '../i18n/I18n.js';
import type { RenderPalette } from '../render/palette.js';
import { RESET_BUTTON_FILL, hitTestResetButton, layoutResetButton } from '../render/ViewportControls.js';
import { TileHoverCursor } from '../ui/TileHoverCursor.js';
import { VIEWPORT_DEFAULT_ZOOM } from '../utils/constants.js';
import type { LayoutManager } from '../layout/LayoutManager.js';
import { findHoveredTile } from './SceneCallbacks.js';

export interface TableViewportOptions {
  readonly canvas: HTMLCanvasElement;
  readonly layoutManager: LayoutManager;
  /** A completed tap that started on the local rack — routes to discard. */
  readonly onTap: (x: number, y: number) => void;
  /** Fired after any pan/zoom/reset — the scene should mark itself dirty. */
  readonly onDirty: () => void;
  /** The live state to hover-resolve against, or null while unmounted. */
  readonly getState: () => MahjongState | null;
  /** The viewer's seat — decides which rack is the sacred tap zone and whose
   *  hand a hover may name. Solo play and the lessons return seat 0; online it
   *  is whatever the server sat us in, hence a getter rather than a value. */
  readonly getLocalSeat: () => Seat;
  /** The pause gear was tapped — the same thing Escape does. */
  readonly onPause: () => void;
}

export class TableViewport {
  readonly viewport = new Viewport();
  private readonly controller: ViewportController;
  private readonly input: InputHandler;
  private readonly hoverCursor: TileHoverCursor;
  private readonly gearTooltip: CanvasTooltip;
  private gearHovered = false;

  constructor(private readonly opts: TableViewportOptions) {
    this.hoverCursor = new TileHoverCursor(opts.canvas);
    this.gearTooltip = new CanvasTooltip(opts.canvas);
    this.controller = new ViewportController({
      viewport: this.viewport,
      isInRackZone: (p) => this.isInRackZone(p),
      buttonAt: (p) => this.buttonAt(p),
      onTap: (x, y) => this.opts.onTap(x, y),
      onViewportChange: () => this.opts.onDirty(),
      onHover: (p) => this.handleHover(p),
    });
    this.input = new InputHandler({
      canvas: opts.canvas,
      getViewport: () => opts.layoutManager.getConfig().viewport,
      onInteraction: (e) => this.controller.handle(e),
      onWheel: (e) => this.controller.handleWheel(e),
    });
  }

  dispose(): void {
    this.input.dispose();
    this.controller.reset();
    this.hoverCursor.dispose();
    this.gearTooltip.dispose();
  }

  /** Draw the pause gear — screen space, top-right corner, over everything else. Call last. */
  drawControls(ctx: CanvasRenderingContext2D, palette: RenderPalette): void {
    const width = this.opts.layoutManager.getConfig().viewport.width;
    drawPauseGear(ctx, width, { fill: RESET_BUTTON_FILL, border: palette.compassText }, { hover: this.gearHovered });
  }

  /** The gear exists once there is a table to draw: before the first online snapshot nothing is on screen. */
  private isOverGear(point: Point): boolean {
    if (this.opts.getState() === null) return false;
    return gearHitTest(point, pauseGearBounds(this.opts.layoutManager.getConfig().viewport.width));
  }

  /** The gear outranks the reset button only by being checked first; they never overlap. */
  private buttonAt(point: Point): CanvasButton | null {
    if (this.isOverGear(point)) return { id: 'pause', run: () => this.pause() };
    return this.isOverResetButton(point) ? { id: 'reset-view', run: () => this.resetView() } : null;
  }

  private pause(): void {
    // The menu covers the gear: drop its hover state so nothing is left lit or labelled.
    this.setGearHovered(false);
    this.opts.onPause();
  }

  private setGearHovered(hovered: boolean): void {
    this.gearTooltip.set(hovered ? t('pause.openTooltip') : null);
    if (hovered === this.gearHovered) return;
    this.gearHovered = hovered;
    this.opts.onDirty();
  }

  private isInRackZone(point: Point): boolean {
    const localSeat = this.opts.getLocalSeat();
    const zone = this.opts.layoutManager.getConfig().seatZones[seatPosition(localSeat, localSeat)];
    return (
      point.x >= zone.x &&
      point.x <= zone.x + zone.width &&
      point.y >= zone.y &&
      point.y <= zone.y + zone.height
    );
  }

  private isOverResetButton(point: Point): boolean {
    const btn = layoutResetButton(this.opts.layoutManager.getConfig().pond);
    return hitTestResetButton(btn, point.x, point.y);
  }

  private handleHover(point: Point | null): void {
    const overGear = point !== null && this.isOverGear(point);
    this.setGearHovered(overGear);
    if (point === null || overGear) {
      this.hoverCursor.update(null, null);
      if (overGear) this.opts.canvas.style.cursor = 'pointer';
      return;
    }
    const state = this.opts.getState();
    const hovered =
      state === null
        ? null
        : findHoveredTile(
            state,
            this.opts.layoutManager.getConfig(),
            this.viewport,
            point,
            this.opts.getLocalSeat(),
          );
    this.hoverCursor.update(hovered, point);
  }

  private resetView(): void {
    this.viewport.resetTo(0, 0, VIEWPORT_DEFAULT_ZOOM);
    this.viewport.clearUserControlled();
    this.opts.onDirty();
  }
}
